/**
 * Global Audio Player
 * Persistent audio player with WaveSurfer.js
 * State persists across page navigation via sessionStorage
 * Syncs with card play buttons across the site
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'audioPlayerState';

  // Selector for any play button on the site
  const PLAY_BUTTON_SELECTOR = '.pp-play-btn, [data-audio-play], .audio-preview-btn';

  // DOM Elements
  let playerEl;
  let waveformEl;
  let playBtn;
  let playIcon;
  let pauseIcon;
  let titleEl;
  let imageEl;
  let currentTimeEl;
  let durationEl;
  let atcBtn;
  let viewBtn;
  let closeBtn;

  // WaveSurfer instance
  let wavesurfer = null;

  // Current state
  let currentState = null;

  /**
   * Initialize the audio player
   */
  function init() {
    // Get DOM elements
    playerEl = document.getElementById('global-audio-player');
    if (!playerEl) return;

    waveformEl = document.getElementById('audio-player-waveform');
    playBtn = document.getElementById('audio-player-play');
    playIcon = playBtn?.querySelector('.play-icon');
    pauseIcon = playBtn?.querySelector('.pause-icon');
    titleEl = document.getElementById('audio-player-title');
    imageEl = document.getElementById('audio-player-img');
    currentTimeEl = document.getElementById('audio-player-current');
    durationEl = document.getElementById('audio-player-duration');
    atcBtn = document.getElementById('audio-player-atc');
    viewBtn = document.getElementById('audio-player-view');
    closeBtn = document.getElementById('audio-player-close');

    // Bind events
    bindEvents();

    // Restore state from sessionStorage
    restoreState();
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    // Play/Pause button
    playBtn?.addEventListener('click', togglePlay);

    // Close button
    closeBtn?.addEventListener('click', closePlayer);

    // Add to Cart button
    atcBtn?.addEventListener('click', addToCart);

    // Listen for play button clicks throughout the page
    document.addEventListener('click', function(e) {
      const playButton = e.target.closest(PLAY_BUTTON_SELECTOR);
      if (playButton) {
        e.preventDefault();
        e.stopPropagation();
        playFromButton(playButton);
      }
    });
  }

  /**
   * Play audio from a play button element
   */
  function playFromButton(button) {
    const audioUrl = button.dataset.audioUrl;
    const productTitle = button.dataset.productTitle;
    const productImage = button.dataset.productImage;
    const productUrl = button.dataset.productUrl;
    const productVariantId = button.dataset.productVariantId;

    if (!audioUrl) {
      console.warn('No audio URL provided');
      return;
    }

    // Check if same audio is already loaded
    if (currentState && currentState.audioUrl === audioUrl && wavesurfer) {
      togglePlay();
      return;
    }

    // Load new audio
    loadAudio({
      audioUrl,
      productTitle,
      productImage,
      productUrl,
      productVariantId
    });
  }

  /**
   * Load audio into the player
   */
  function loadAudio(data) {
    // Reset all card buttons first
    resetAllCardButtons();

    currentState = {
      audioUrl: data.audioUrl,
      productTitle: data.productTitle || 'Unknown Track',
      productImage: data.productImage || '',
      productUrl: data.productUrl || '#',
      productVariantId: data.productVariantId || '',
      currentTime: 0,
      isPlaying: true
    };

    // Update UI
    titleEl.textContent = currentState.productTitle;
    imageEl.src = currentState.productImage;
    imageEl.alt = currentState.productTitle;
    viewBtn.href = currentState.productUrl;

    // Check if we're on the product page
    updateViewButtonVisibility();

    // Show player
    showPlayer();

    // Initialize or update WaveSurfer
    initWaveSurfer(currentState.audioUrl);

    // Save state
    saveState();
  }

  /**
   * Initialize WaveSurfer
   */
  function initWaveSurfer(audioUrl, seekTo = 0, autoPlay = true) {
    // Destroy existing instance
    if (wavesurfer) {
      wavesurfer.destroy();
    }

    // Wait for WaveSurfer to be available
    if (typeof WaveSurfer === 'undefined') {
      setTimeout(() => initWaveSurfer(audioUrl, seekTo, autoPlay), 100);
      return;
    }

    // Create new instance
    wavesurfer = WaveSurfer.create({
      container: waveformEl,
      waveColor: 'rgba(255, 255, 255, 0.3)',
      progressColor: '#ccf832',
      cursorColor: 'transparent',
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 36,
      responsive: true,
      normalize: true
    });

    // Load audio
    wavesurfer.load(audioUrl);

    // Event: Ready
    wavesurfer.on('ready', function() {
      updateDuration();

      if (seekTo > 0) {
        const duration = wavesurfer.getDuration();
        wavesurfer.seekTo(seekTo / duration);
      }

      if (autoPlay) {
        wavesurfer.play();
        updatePlayButton(true);
        updateCardButtons(true);
      }
    });

    // Event: Audio process (time update)
    wavesurfer.on('audioprocess', function() {
      updateCurrentTime();
      saveState();
    });

    // Event: Seek
    wavesurfer.on('seeking', function() {
      updateCurrentTime();
    });

    // Event: Play
    wavesurfer.on('play', function() {
      updatePlayButton(true);
      updateCardButtons(true);
      if (currentState) currentState.isPlaying = true;
      saveState();
    });

    // Event: Pause
    wavesurfer.on('pause', function() {
      updatePlayButton(false);
      updateCardButtons(false);
      if (currentState) currentState.isPlaying = false;
      saveState();
    });

    // Event: Finish
    wavesurfer.on('finish', function() {
      updatePlayButton(false);
      updateCardButtons(false);
      if (currentState) currentState.isPlaying = false;
      saveState();
    });

    // Event: Error
    wavesurfer.on('error', function(err) {
      console.error('WaveSurfer error:', err);
    });
  }

  /**
   * Toggle play/pause
   */
  function togglePlay() {
    if (!wavesurfer) return;
    wavesurfer.playPause();
  }

  /**
   * Update the main player play button icon
   */
  function updatePlayButton(isPlaying) {
    if (isPlaying) {
      playIcon.style.display = 'none';
      pauseIcon.style.display = 'block';
    } else {
      playIcon.style.display = 'block';
      pauseIcon.style.display = 'none';
    }
  }

  /**
   * Update all card buttons that match current audio URL
   */
  function updateCardButtons(isPlaying) {
    if (!currentState || !currentState.audioUrl) return;

    // Find all play buttons with matching audio URL
    const buttons = document.querySelectorAll(PLAY_BUTTON_SELECTOR);

    buttons.forEach(button => {
      const buttonAudioUrl = button.dataset.audioUrl;
      const buttonPlayIcon = button.querySelector('.pp-play-icon, .play-icon');
      const buttonPauseIcon = button.querySelector('.pp-pause-icon, .pause-icon');

      if (buttonAudioUrl === currentState.audioUrl) {
        // This button matches the current playing audio
        button.classList.add('is-playing');
        if (buttonPlayIcon) buttonPlayIcon.style.display = isPlaying ? 'none' : 'block';
        if (buttonPauseIcon) buttonPauseIcon.style.display = isPlaying ? 'block' : 'none';
      } else {
        // Different audio - make sure it shows play icon
        button.classList.remove('is-playing');
        if (buttonPlayIcon) buttonPlayIcon.style.display = 'block';
        if (buttonPauseIcon) buttonPauseIcon.style.display = 'none';
      }
    });
  }

  /**
   * Reset all card buttons to show play icon
   */
  function resetAllCardButtons() {
    const buttons = document.querySelectorAll(PLAY_BUTTON_SELECTOR);

    buttons.forEach(button => {
      button.classList.remove('is-playing');
      const buttonPlayIcon = button.querySelector('.pp-play-icon, .play-icon');
      const buttonPauseIcon = button.querySelector('.pp-pause-icon, .pause-icon');
      if (buttonPlayIcon) buttonPlayIcon.style.display = 'block';
      if (buttonPauseIcon) buttonPauseIcon.style.display = 'none';
    });
  }

  /**
   * Update current time display
   */
  function updateCurrentTime() {
    if (!wavesurfer) return;
    const time = wavesurfer.getCurrentTime();
    currentTimeEl.textContent = formatTime(time);
    if (currentState) {
      currentState.currentTime = time;
    }
  }

  /**
   * Update duration display
   */
  function updateDuration() {
    if (!wavesurfer) return;
    const duration = wavesurfer.getDuration();
    durationEl.textContent = formatTime(duration);
  }

  /**
   * Format time in M:SS
   */
  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Show the player
   */
  function showPlayer() {
    playerEl.style.display = 'block';
    // Trigger reflow
    playerEl.offsetHeight;
    playerEl.classList.add('is-visible');
  }

  /**
   * Hide the player
   */
  function hidePlayer() {
    playerEl.classList.remove('is-visible');
    setTimeout(() => {
      playerEl.style.display = 'none';
    }, 300);
  }

  /**
   * Close the player
   */
  function closePlayer() {
    if (wavesurfer) {
      wavesurfer.pause();
      wavesurfer.destroy();
      wavesurfer = null;
    }

    // Reset all card buttons
    resetAllCardButtons();

    currentState = null;
    clearState();
    hidePlayer();
  }

  /**
   * Add to cart
   */
  function addToCart() {
    if (!currentState || !currentState.productVariantId) {
      console.warn('No product variant ID');
      return;
    }

    const variantId = currentState.productVariantId;

    // Disable button temporarily
    atcBtn.disabled = true;
    atcBtn.textContent = 'Adding...';

    // Add to cart via Shopify AJAX API
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
    .then(response => response.json())
    .then(data => {
      atcBtn.textContent = 'Added!';

      // Try to open cart drawer (theme-specific)
      openCartDrawer();

      // Reset button after delay
      setTimeout(() => {
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to Cart';
      }, 2000);
    })
    .catch(error => {
      console.error('Add to cart error:', error);
      atcBtn.disabled = false;
      atcBtn.textContent = 'Add to Cart';
    });
  }

  /**
   * Open cart drawer (theme-specific)
   * This tries multiple common methods
   */
  function openCartDrawer() {
    // Method 1: Dispatch custom event (Dawn theme and others)
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // Method 2: Click cart icon to open drawer
    const cartToggle = document.querySelector('[data-cart-toggle], .cart-drawer-trigger, .js-drawer-open-cart, [href="/cart"]');
    if (cartToggle && cartToggle.click) {
      // Small delay to let cart update first
      setTimeout(() => {
        // Check if there's a drawer trigger specifically
        const drawerTrigger = document.querySelector('.js-drawer-open-cart, [data-drawer-open="cart"]');
        if (drawerTrigger) {
          drawerTrigger.click();
        }
      }, 300);
    }

    // Method 3: Trigger Shopify Section Rendering API
    fetch('/?section_id=cart-drawer')
      .then(response => response.text())
      .then(html => {
        // Some themes listen for this
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(() => {});
  }

  /**
   * Update view button visibility
   * Hide if we're on the product page
   */
  function updateViewButtonVisibility() {
    if (!currentState || !viewBtn) return;

    const currentPath = window.location.pathname;
    const productPath = currentState.productUrl;

    // Extract just the path without query params
    const cleanProductPath = productPath.split('?')[0];

    if (currentPath === cleanProductPath || currentPath.includes(cleanProductPath)) {
      viewBtn.classList.add('is-hidden');
    } else {
      viewBtn.classList.remove('is-hidden');
    }
  }

  /**
   * Save state to sessionStorage
   */
  function saveState() {
    if (!currentState) return;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
    } catch (e) {
      console.warn('Could not save audio player state:', e);
    }
  }

  /**
   * Clear state from sessionStorage
   */
  function clearState() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Could not clear audio player state:', e);
    }
  }

  /**
   * Restore state from sessionStorage
   */
  function restoreState() {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (!saved) return;

      const state = JSON.parse(saved);
      if (!state || !state.audioUrl) return;

      currentState = state;

      // Update UI
      titleEl.textContent = currentState.productTitle;
      imageEl.src = currentState.productImage;
      imageEl.alt = currentState.productTitle;
      viewBtn.href = currentState.productUrl;

      // Check view button visibility
      updateViewButtonVisibility();

      // Show player
      showPlayer();

      // Initialize WaveSurfer with saved position
      initWaveSurfer(
        currentState.audioUrl,
        currentState.currentTime || 0,
        currentState.isPlaying || false
      );

      // Update card buttons to reflect current state after a short delay
      // (to allow DOM to be fully ready)
      setTimeout(() => {
        updateCardButtons(currentState.isPlaying || false);
      }, 100);

    } catch (e) {
      console.warn('Could not restore audio player state:', e);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
